import React, { useEffect } from 'react'
import { Avatar, Popconfirm, Tooltip } from 'antd'
import { CalendarBlank, Trash, MapPin, Clock } from '@phosphor-icons/react'
import moment from 'moment'
import { leagueEventStore } from 'state/league/leagueEvent'
import { useGetLeagueTeamConstraintsById, useDeleteTeamLeagueConstraint } from 'api/league/LeagueCreation/leagueTeamConstraints'
import { convertMinutesToLocalTime, getNameSignature } from 'helper/utility'

const DAY_ORDER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const ConstraintsList: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))

  const {
    data: constraints,
    isLoading,
    refetch,
  } = useGetLeagueTeamConstraintsById(leagueData._id)

  const { mutate: deleteConstraint } = useDeleteTeamLeagueConstraint()

  useEffect(() => {
    refetch()
  }, [])

  if (isLoading) return null

  if (!constraints || constraints.length === 0) {
    return (
      <div className="constraints-empty">
        <span className="text-14 text-gray">No constraints added yet.</span>
      </div>
    )
  }

  return (
    <div className="constraints-section">
      <div className="constraints-section-header">
        <span className="text-14 w-600">Team constraints</span>
        <span className="constraints-count-badge">{constraints.length}</span>
      </div>

      <div className="constraints-cards-grid">
        {constraints.map(c => (
          <div key={c._id} className="constraint-card">

            {/* Header: team + delete */}
            <div className="constraint-card-header">
              <div className="constraint-team-info">
                <Avatar size={32} src={c.team?.profilePhoto?.thumb}>
                  {getNameSignature(c.team?.name || '?')}
                </Avatar>
                <span className="text-14 w-600">{c.team?.name || '—'}</span>
              </div>
              <Popconfirm
                title="Remove this constraint?"
                okText="Remove"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                onConfirm={() => deleteConstraint(c._id)}
              >
                <button className="constraint-delete-btn">
                  <Trash size={15} />
                </button>
              </Popconfirm>
            </div>

            {/* Venue */}
            {c.venue?.title && (
              <div className="constraint-venue">
                <MapPin size={13} weight="fill" />
                <span className="text-12 text-gray">{c.venue.title}</span>
              </div>
            )}

            {/* Time range */}
            {c.timings && (
              <div className="constraint-time">
                <Clock size={13} weight="fill" />
                <span className="text-12 text-gray">
                  {convertMinutesToLocalTime(c.timings.startTime)} – {convertMinutesToLocalTime(c.timings.endTime)}
                </span>
              </div>
            )}

            {/* Reason */}
            {c.reason && (
              <div className="constraint-reason">
                <span className="text-12 text-gray">{c.reason}</span>
              </div>
            )}

            {/* Recurring weekly days */}
            {c.days && c.days.length > 0 && (
              <div className="constraint-block">
                <span className="constraint-block-label">Weekly (recurring)</span>
                <div className="constraint-days-row">
                  {DAY_ORDER.map(day => (
                    <Tooltip key={day} title={day} mouseEnterDelay={0.4}>
                      <span
                        className={`constraint-day-pill ${c.days.includes(day) ? 'active' : 'inactive'}`}
                      >
                        {day}
                      </span>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}

            {/* One-off specific dates */}
            {c.dates && c.dates.length > 0 && (
              <div className="constraint-block">
                <span className="constraint-block-label">Specific dates</span>
                <div className="constraint-dates-row">
                  <CalendarBlank size={13} className="constraint-dates-icon" />
                  <div className="constraint-date-tags">
                    {c.dates.map(d => (
                      <span key={d} className="constraint-date-pill">
                        {moment(d).utc().format('MMM DD')}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>
        ))}
      </div>
    </div>
  )
}

export default ConstraintsList
